import { ReactNode, useEffect, useMemo, useState } from "react";
import { useCreateTask } from "@api/task.api";
import { Task, TaskType } from "@common/types/task";
import { useFormik, FormikProvider } from "formik";
import { TaskCreateSchema, TaskCreate } from "@common/contracts/task.contract";
import z from "zod";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { pluralizeWithCount } from "@common/helpers/string";
import { PersistableView } from "@common/types/views/views";
import { SelectedTransaction } from "@components/transactions/TransactionsBulkActions/transactionsBulkActionsContext";
import { Separator } from "@design-system/Separator/Separator";
import { useConstant } from "@hooks/useConstant";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import styles from "./CreateTaskDrawer.module.scss";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useCreatedTaskToast } from "./useCreatedTaskToast";
import { TaskCreationFormTopSection } from "./TaskCreationFormTopSection";
import { TaskCreationFormDetails } from "./TaskCreationFormDetails";
import { PerEntityTaskConfig } from "./taskCreation.types";
import {
    canShowWorkflowPicker,
    getInitialWorkflowView,
    getCreateTaskBlocker,
    getWorkflowViewForType,
    hasTaskCreationContext,
    getSelectionTaskView,
    withTaskViewEntities,
} from "./taskWorkflow";
import { getCreateTaskLabel } from "./createTaskLabel";
import { TaskCreationFormFooter } from "./TaskCreationFormFooter";
import { getTaskPreviewDefinition } from "./taskCreationPreviewRegistry";
import {
    buildGroupTaskValues,
    getGroupTaskCount,
    getPerTransactionFilters,
    getTotalTaskCount,
    getTransactionSplitBlocker,
    isTaskSplitPerTransaction,
    withoutTaskRelations,
} from "./taskSplit";

interface TaskCreationFormProps {
    taskType: TaskType;
    onSuccess?: () => void;
    viewConfig?: PersistableView;
    transactionsTask?: {
        transactions?: SelectedTransaction[];
        transactionsCount?: number;
    };
    initialValues?: Partial<Omit<TaskCreate, "type">>;
    customOnSubmit?: (values: TaskCreate) => Promise<Task>;
    hideEntitySelection?: boolean;
    hideGroupSelection?: boolean;
    preview?: ReactNode;
    isSelectionPreview?: boolean;
    relatedTaskId?: string;
    perEntityTask?: PerEntityTaskConfig;
    onWorkflowChange?: (type: TaskType) => void;
}

export const TaskCreationForm = ({
    taskType,
    onSuccess,
    viewConfig,
    transactionsTask,
    initialValues: providedInitialValues,
    customOnSubmit,
    hideEntitySelection,
    hideGroupSelection,
    preview,
    isSelectionPreview = false,
    relatedTaskId,
    perEntityTask,
    onWorkflowChange,
}: TaskCreationFormProps) => {
    const [loading, setLoading] = useState(false);
    const [createdEntityIds, setCreatedEntityIds] = useState<Set<number>>(
        () => new Set(),
    );
    const [createMore, setCreateMore] = useState(false);
    // Bumped after each "create more" submit to force the (uncontrolled)
    // description editor to remount with empty content, since it only reads
    // its initial value once on mount.
    const [descriptionResetKey, setDescriptionResetKey] = useState(0);

    // The drawer can be reopened without unmounting (the overlay close is
    // deferred by an animation), so reset the per-entity dedup tracking
    // whenever a new per-entity config is supplied to avoid silently skipping
    // entities from a previous submit.
    useEffect(() => {
        setCreatedEntityIds(new Set());
    }, [perEntityTask]);

    const accounts = useFinancialAccounts();
    const createTask = useCreateTask();
    const showCreatedTaskToast = useCreatedTaskToast();
    const { error, toast } = useToaster();

    const hasContext = hasTaskCreationContext({
        viewConfig,
        transactionsTask,
        perEntityTask,
        relatedTaskId,
        customOnSubmit,
    });

    const mountedAt = useConstant(() => new Date());
    const initialValues = useMemo(
        () => ({
            title: "",
            view:
                viewConfig ??
                (hasContext
                    ? getInitialWorkflowView({ taskType, now: mountedAt })
                    : getWorkflowViewForType({
                          type: taskType,
                          currentView: undefined,
                          now: mountedAt,
                      })),
            ...(relatedTaskId ? { relatedTaskIds: [relatedTaskId] } : {}),
            ...providedInitialValues,
            type: taskType,
        }),
        [
            taskType,
            viewConfig,
            hasContext,
            providedInitialValues,
            relatedTaskId,
            mountedAt,
        ],
    );

    const entityGroups = perEntityTask?.entityGroups ?? [];
    const transactionCount = transactionsTask?.transactions?.length ?? 0;
    const hasManuallySelectedTransactions = transactionCount > 1;

    const showWorkflowPicker =
        isSelectionPreview || canShowWorkflowPicker({ taskType, hasContext });

    const formik = useFormik<z.infer<typeof TaskCreateSchema>>({
        initialValues,
        validate: (values) =>
            validateFormikWithZodSchema(TaskCreateSchema)(
                isTaskSplitPerTransaction({
                    values,
                    hasManuallySelectedTransactions,
                })
                    ? withoutTaskRelations(values)
                    : values,
            ),
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: async (values, formikHelpers) => {
                const isPerTransaction = isTaskSplitPerTransaction({
                    values,
                    hasManuallySelectedTransactions,
                });
                const totalTaskCount = getTotalTaskCount({
                    entityGroups,
                    isPerTransaction,
                    transactionCount,
                });
                const splitBlocker = isPerTransaction
                    ? getTransactionSplitBlocker({
                          entityGroups,
                          transactionCount,
                      })
                    : null;
                if (splitBlocker) {
                    error(splitBlocker);
                    return;
                }

                if (perEntityTask && entityGroups.length > 1) {
                    // Per-entity mode: create one task per entity. Skip entities
                    // whose task already succeeded on a previous submit so a
                    // retry after a partial failure does not create duplicates.
                    const pendingGroups = entityGroups.filter(
                        (group) => !createdEntityIds.has(group.entityId),
                    );
                    let createdCount = entityGroups
                        .filter((group) => createdEntityIds.has(group.entityId))
                        .reduce(
                            (count, group) =>
                                count +
                                getGroupTaskCount({ group, isPerTransaction }),
                            0,
                        );
                    for (const group of pendingGroups) {
                        const taskValues = buildGroupTaskValues({
                            values,
                            group,
                            baseView:
                                values.view ?? perEntityTask.baseViewConfig,
                            isPerTransaction,
                        });
                        try {
                            if (customOnSubmit) {
                                await customOnSubmit(taskValues);
                            } else {
                                await createTask.mutateAsync(taskValues);
                            }
                            // Record the entity as soon as the task is created so
                            // a retry never re-creates it.
                            createdCount += getGroupTaskCount({
                                group,
                                isPerTransaction,
                            });
                            setCreatedEntityIds((prev) =>
                                new Set(prev).add(group.entityId),
                            );
                        } catch {
                            error(
                                `Created ${createdCount} of ${pluralizeWithCount(
                                    "task",
                                    totalTaskCount,
                                )}. Retry to create the remaining ${
                                    totalTaskCount - createdCount
                                }.`,
                            );
                            return;
                        }
                    }
                    toast(
                        `Created ${pluralizeWithCount("task", totalTaskCount)}`,
                    );
                } else {
                    // Single-task path (existing logic)
                    const baseView = isSelectionPreview
                        ? getSelectionTaskView(values)
                        : withTaskViewEntities(values);
                    const submittedValues = {
                        ...(isPerTransaction
                            ? withoutTaskRelations(values)
                            : values),
                        createTaskPerTransaction:
                            hasManuallySelectedTransactions
                                ? values.createTaskPerTransaction
                                : undefined,
                        view:
                            isPerTransaction && values.view
                                ? {
                                      ...values.view,
                                      filters: getPerTransactionFilters(
                                          values.view.filters,
                                      ),
                                  }
                                : baseView,
                    };
                    const task = customOnSubmit
                        ? await customOnSubmit(submittedValues)
                        : await createTask.mutateAsync(submittedValues);
                    showCreatedTaskToast(task);
                }

                if (createMore) {
                    // Keep the drawer open and clear only the title and
                    // description, preserving every other field for the next task.
                    // Also clear per-entity dedup so the next batch is not skipped.
                    setCreatedEntityIds(new Set());
                    await formikHelpers.setFieldValue("title", "");
                    await formikHelpers.setFieldValue("description", null);
                    await formikHelpers.setFieldValue(
                        "descriptionType",
                        undefined,
                    );
                    setDescriptionResetKey((key) => key + 1);
                    return;
                }

                onSuccess?.();
            },
        }),
    });

    const isPerTransaction = isTaskSplitPerTransaction({
        values: formik.values,
        hasManuallySelectedTransactions,
    });
    const createTaskLabel = getCreateTaskLabel({
        taskCount: getTotalTaskCount({
            entityGroups,
            isPerTransaction,
            transactionCount,
        }),
    });

    const resetDescription = () => setDescriptionResetKey((key) => key + 1);

    const formElement = (
        <form
            onSubmit={(e) => {
                const blocker = getCreateTaskBlocker({
                    ...formik.values,
                    accounts,
                });
                if (blocker) {
                    e.preventDefault();
                    error(blocker);
                    return;
                }
                formik.handleSubmit(e);
            }}
            className={styles.formContainer}
        >
            <TaskCreationFormTopSection
                transactionsTask={transactionsTask}
                hideAttachment={isSelectionPreview}
                descriptionResetKey={descriptionResetKey}
                isWorkflowLocked={isSelectionPreview || !showWorkflowPicker}
                onWorkflowChange={onWorkflowChange}
                onTemplateApplied={resetDescription}
            />
            <Separator />
            <TaskCreationFormDetails
                hideEntitySelection={
                    hideEntitySelection ||
                    (perEntityTask?.entityGroups.length ?? 0) > 1
                }
                hideGroupSelection={hideGroupSelection}
                hideRelations={isPerTransaction}
                showWorkflowPicker={showWorkflowPicker}
                isWorkflowLocked={isSelectionPreview}
                onWorkflowChange={onWorkflowChange}
                onTemplateApplied={resetDescription}
                splitOptions={
                    isSelectionPreview &&
                    getTaskPreviewDefinition(taskType)?.supportsSplitOptions
                        ? {
                              transactionSplitBlocker:
                                  getTransactionSplitBlocker({
                                      entityGroups,
                                      transactionCount,
                                  }),
                          }
                        : undefined
                }
            />
            <TaskCreationFormFooter
                isCreateMore={createMore}
                onCreateMoreChange={setCreateMore}
                isSubmitting={
                    formik.isSubmitting ||
                    (!customOnSubmit && createTask.isPending)
                }
                submitLabel={createTaskLabel}
            />
        </form>
    );

    return (
        <FormikProvider value={formik}>
            {preview ? (
                <div className={styles.previewLayout}>
                    {preview}
                    <div className={styles.formPane}>{formElement}</div>
                </div>
            ) : (
                formElement
            )}
        </FormikProvider>
    );
};
